// 通常入札タブとブロック入札タブで共有する入札一覧の絞り込み (AC-BSP-019 の売/買・ステータス・ワークフロー)。
//
// 選択状態は page が 1 つだけ持ち、 両タブの一覧と一括取下の対象 (AC-BSP-116) が同じ関数で絞る。
// ステータスは一覧のステータス列と同じ区分・同じ締切判定で判定する (選んだ区分とバッジを食い違わせない)。
// 保持 (AC-SC-031) はタブ固有の絞り込みと合わせて `bidListFilters.ts` が持つ (#12745)。
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

import type { BlockBidRow } from './blockBidRow'
import {
  type BlockBidStatusCategoryInput,
  deriveBlockBidStatusCategory,
} from './blockBidStatusCategory'
import type { SpotBidRow } from './spotBidRow'
import {
  type SpotBidStatusCategory,
  type SpotBidStatusCategoryInput,
  deriveSpotBidStatusCategory,
} from './spotBidStatusCategory'

/** 両タブで共有する入札一覧の絞り込み。 どの軸も空集合は「すべて」。 */
export type SharedBidListFilter = {
  readonly sides: ReadonlySet<Side>
  /** 選択肢は AC-BSP-019a の 11 区分 ([[SPOT_BID_STATUS_FILTER_CATEGORIES]])。 両タブで同じ。 */
  readonly statuses: ReadonlySet<SpotBidStatusCategory>
  /** ワークフロー ID。 ワークフロー ID の無い札は [[NO_WORKFLOW_FILTER_VALUE]] で選ぶ。 */
  readonly workflows: ReadonlySet<string>
}

/** ワークフローフィルタの「ワークフローなし」 の値 (AC-BSP-019)。 札のワークフロー ID と重ならない値にする。 */
export const NO_WORKFLOW_FILTER_VALUE = '(none)'

/** 札のワークフロー ID を、 ワークフローフィルタで照合する値にする (ID の無い札は「ワークフローなし」)。 */
export const workflowFilterValueOf = (
  workflowId: string | undefined,
): string =>
  workflowId === undefined || workflowId === ''
    ? NO_WORKFLOW_FILTER_VALUE
    : workflowId

/**
 * ワークフローフィルタの選択肢にするワークフロー ID (重複なし・昇順。 「ワークフローなし」 は含めない)。
 * 選択は両タブで共有するので、 通常入札・ブロック入札の両方の札から集める。
 */
export const collectWorkflowFilterIds = (
  bids: ReadonlyArray<Pick<SpotBidRow, 'workflowId'>>,
  blockRows: ReadonlyArray<Pick<BlockBidRow, 'workflowId'>>,
): string[] => {
  const ids = new Set<string>()
  for (const row of [...bids, ...blockRows]) {
    if (row.workflowId !== undefined && row.workflowId !== '')
      ids.add(row.workflowId)
  }
  return [...ids].sort()
}

/**
 * 絞り込みなし。 「クリア」 はこのインスタンスに戻す (毎回新しい値を作ると、 既に空でも参照が変わって
 * 一覧の再計算と、 それに依存する effect が走り直すため)。
 */
export const EMPTY_SHARED_BID_LIST_FILTER: SharedBidListFilter = {
  sides: new Set(),
  statuses: new Set(),
  workflows: new Set(),
}

/**
 * 入札 CSV で取り込んだ札がなりうるステータス。 取り込んだ札は行ごとのエリアの承認モードで、
 * 要承認は承認待ち、 承認なし・自動は提出して送信中・入札中、 予約入札フェーズの受渡日は予約中になる
 * (spot-bid-normal-csv-import.md §4)。 取込結果は札ごとの区分を持たないので、 なりうる区分をすべて足す。
 */
const CSV_IMPORTED_BID_STATUSES: readonly SpotBidStatusCategory[] = [
  'pendingApproval',
  'sending',
  'active',
  'reserved',
]

const SIDE_OF_IMPORTED: Record<'buy' | 'sell', Side> = {
  buy: Side.BUY,
  sell: Side.SELL,
}

/**
 * 入札 CSV を取り込んで移ってきた時に、 取り込んだ札が一覧に出るよう共有の絞り込みを広げる
 * (AC-SPOT-024: 今の選択に足し、 選択を狭めない)。 「すべて」 (空) の項目はそのまま。 売/買は
 * 取り込んだ札の売買を、 ステータスは取り込んだ札がなりうる区分を、 ワークフローは「ワークフローなし」
 * を足す (取り込んだ札はワークフローに紐付かない)。 足すものが無ければ
 * undefined (呼び出し側は保存し直さない)。
 */
export function widenSharedBidListFilterForCsvImport(
  filter: SharedBidListFilter,
  importedSides: ReadonlyArray<'buy' | 'sell'>,
): SharedBidListFilter | undefined {
  const sides =
    filter.sides.size === 0
      ? filter.sides
      : new Set([
          ...filter.sides,
          ...importedSides.map((side) => SIDE_OF_IMPORTED[side]),
        ])
  const statuses =
    filter.statuses.size === 0
      ? filter.statuses
      : new Set([...filter.statuses, ...CSV_IMPORTED_BID_STATUSES])
  const workflows =
    filter.workflows.size === 0
      ? filter.workflows
      : new Set([...filter.workflows, NO_WORKFLOW_FILTER_VALUE])
  if (
    sides.size === filter.sides.size &&
    statuses.size === filter.statuses.size &&
    workflows.size === filter.workflows.size
  ) {
    return undefined
  }
  return { sides, statuses, workflows }
}

export const isSharedBidListFilterActive = (
  filter: SharedBidListFilter,
): boolean =>
  filter.sides.size > 0 || filter.statuses.size > 0 || filter.workflows.size > 0

const matchesAll = <T>(selected: ReadonlySet<T>, value: T): boolean =>
  selected.size === 0 || selected.has(value)

/** 通常入札の札が共有の絞り込みに合うか。 */
export const matchesSharedBidListFilter = (
  bid: Pick<SpotBidRow, 'direction' | 'workflowId'> &
    SpotBidStatusCategoryInput,
  filter: SharedBidListFilter,
  isPastDeadline: boolean,
): boolean =>
  matchesAll(filter.sides, bid.direction) &&
  matchesAll(filter.workflows, workflowFilterValueOf(bid.workflowId)) &&
  (filter.statuses.size === 0 ||
    filter.statuses.has(deriveSpotBidStatusCategory(bid, isPastDeadline)))

const matchesBlockLeg = (
  leg: Pick<BlockBidRow, 'direction' | 'workflowId'> &
    BlockBidStatusCategoryInput,
  filter: SharedBidListFilter,
  isPastDeadline: boolean,
): boolean =>
  matchesAll(filter.sides, leg.direction) &&
  matchesAll(filter.workflows, workflowFilterValueOf(leg.workflowId)) &&
  (filter.statuses.size === 0 ||
    filter.statuses.has(deriveBlockBidStatusCategory(leg, isPastDeadline)))

/**
 * ブロック入札の Leg 行のうち、 `isLegInScope` と共有の絞り込みの両方に**同じ Leg** が合うグループの
 * 全 Leg (入力順のまま)。 LINK / LOOP は 2 本で 1 組なので、 片方の Leg だけが合っても組の両 Leg を出す
 * (AC-BSP-019 末尾。 一括取下 AC-BSP-116 と同じ規則)。 売/買は Leg ごとに違いうるため、 エリア等の範囲を
 * グループ単位で見てから重ねると、 範囲に入る Leg と売/買が合う Leg が別の組まで出してしまう。
 */
export const filterBlockGroupsBySharedListFilter = <
  R extends Pick<BlockBidRow, 'groupId' | 'direction' | 'workflowId'> &
    BlockBidStatusCategoryInput,
>(
  rows: readonly R[],
  filter: SharedBidListFilter,
  isPastDeadline: boolean,
  isLegInScope: (leg: R) => boolean,
): R[] => {
  const groupIds = new Set(
    rows
      .filter(
        (leg) =>
          isLegInScope(leg) && matchesBlockLeg(leg, filter, isPastDeadline),
      )
      .map((leg) => leg.groupId),
  )
  return rows.filter((row) => groupIds.has(row.groupId))
}
